import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Bouton, Champ, GrilleFormulaire, Message } from '@journal/ui';
import { CoquilleAuth, LienAuth } from '../components/CoquilleAuth.tsx';
import { messageErreur, useSession } from '../lib/session.tsx';
import {
  AIDE_MOT_DE_PASSE, LONGUEUR_MINIMALE_MOT_DE_PASSE,
} from '../lib/mot-de-passe.ts';

/**
 * Écrans du parcours de compte, hors connexion.
 *
 * Ils partagent une même contrainte : on y arrive depuis un courriel, souvent
 * sur un autre appareil que celui d'origine, parfois des jours plus tard. Aucun
 * ne peut donc s'appuyer sur un état conservé côté client — tout ce dont ils
 * disposent tient dans l'URL.
 */


/**
 * Enveloppe commune.
 *
 * Elle délègue toute la mise en page à `CoquilleAuth`, partagée avec la
 * connexion et l'inscription : ces écrans s'atteignent en série — on crée un
 * espace, on confirme son adresse, on choisit un mot de passe. Une enveloppe
 * différente à chaque étape donnerait l'impression de changer de site en cours
 * de route, exactement le doute qu'un lien reçu par courriel doit éviter.
 */
function Encadre({
  titre, sousTitre, children, lienConnexion = true,
}: {
  titre: string;
  sousTitre?: ReactNode;
  children: ReactNode;
  lienConnexion?: boolean;
}) {
  return (
    <CoquilleAuth
      titre={titre}
      {...(sousTitre ? { sousTitre } : {})}
      {...(lienConnexion
        ? { pied: <p><LienAuth vers="/">Retour à la connexion</LienAuth></p> }
        : {})}
    >
      {children}
    </CoquilleAuth>
  );
}

// ── Après inscription ────────────────────────────────────────────────────────

export function ConfirmationInscription() {
  const [parametres] = useSearchParams();
  const email = parametres.get('email');

  return (
    <Encadre titre="Vérifiez votre boîte aux lettres">
      <div className="flex flex-col gap-3">
        <Message>
          {email
            ? `Un courriel de confirmation vient d'être envoyé à ${email}.`
            : "Un courriel de confirmation vient d'être envoyé."}
        </Message>
        <Message>
          Ouvrez-le pour activer votre espace. Sans confirmation, le lien reste
          valable vingt-quatre heures.
        </Message>
        {/* Dire où chercher évite l'essentiel des demandes de support :
            le premier courriel d'un domaine récent finit souvent en
            indésirables. */}
        <Message ton="attention">
          Rien reçu au bout de quelques minutes ? Pensez au dossier des
          courriers indésirables.
        </Message>
        {/*
          * ── La sortie de secours, dite AU MOMENT où elle peut servir ───────
          * C'est ici que se perd le compte payé dont la validation n'aboutit
          * pas : l'adresse comporte une faute de frappe, le courriel n'arrive
          * jamais, et la personne n'a plus aucun moyen de le signaler — elle
          * n'a pas de session, donc pas de support. Un lien sur cet écran-là
          * vaut mieux qu'une adresse enfouie dans les mentions légales.
          */}
        <p className="text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
          {'Un problème de paiement ou d’accès ? '}
          <Link to="/contact" className="underline">
            Écrivez-nous
          </Link>
          {', nous retrouverons votre compte.'}
        </p>
      </div>
    </Encadre>
  );
}

// ── Vérification d'adresse ───────────────────────────────────────────────────

type EtatVerification = 'encours' | 'succes' | 'echec';

export function VerificationEmail() {
  const { api } = useSession();
  const [parametres] = useSearchParams();
  const jeton = parametres.get('token');

  const [etat, setEtat] = useState<EtatVerification>('encours');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!jeton) {
      setEtat('echec');
      setErreur('Lien de vérification incomplet.');
      return;
    }
    let annule = false;
    void api
      .requete('/auth/verify-email', {
        methode: 'POST',
        corps: { token: jeton },
        sansRafraichissement: true,
      })
      .then(
        () => { if (!annule) setEtat('succes'); },
        (e: unknown) => {
          if (annule) return;
          setEtat('echec');
          setErreur(messageErreur(e));
        },
      );
    return () => { annule = true; };
  }, [api, jeton]);

  return (
    <Encadre titre="Confirmation de votre adresse">
      {etat === 'encours' && <Message>Vérification en cours…</Message>}
      {etat === 'succes' && (
        <div className="flex flex-col gap-3">
          <Message ton="succes">Votre adresse est confirmée.</Message>
          <Message>Vous pouvez maintenant vous connecter.</Message>
        </div>
      )}
      {etat === 'echec' && (
        <div className="flex flex-col gap-3">
          <Message ton="erreur">{erreur}</Message>
          {/* Un lien expiré est le cas le plus fréquent : on dit quoi faire
              plutôt que de laisser l'utilisateur sur un constat d'échec. */}
          <Message>
            Ce lien a peut-être déjà servi ou expiré. Connectez-vous pour en
            demander un nouveau.
          </Message>
        </div>
      )}
    </Encadre>
  );
}

// ── Mot de passe oublié ──────────────────────────────────────────────────────

export function MotDePasseOublie() {
  const { api } = useSession();
  const [email, setEmail] = useState('');
  const [envoye, setEnvoye] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.requete('/auth/forgot-password', {
        methode: 'POST',
        corps: { email },
        sansRafraichissement: true,
      });
      setEnvoye(true);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  if (envoye) {
    return (
      <Encadre titre="Demande enregistrée">
        {/*
          * Message volontairement identique que le compte existe ou non.
          * L'API répond déjà de façon indifférenciée ; afficher « aucun compte
          * pour cette adresse » ici annulerait cette protection et
          * transformerait le formulaire en outil d'énumération des comptes.
          */}
        <Message>
          Si un compte existe pour cette adresse, un courriel de
          réinitialisation vient d'être envoyé.
        </Message>
      </Encadre>
    );
  }

  return (
    <Encadre titre="Mot de passe oublié">
      <form onSubmit={soumettre} className="flex flex-col gap-4" noValidate>
        <Message>
          Indiquez votre adresse : vous recevrez un lien pour choisir un
          nouveau mot de passe.
        </Message>
        <Champ
          libelle="Adresse électronique"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          autoFocus
          required
        />
        {erreur && <Message ton="erreur">{erreur}</Message>}
        <Bouton
          type="submit"
          variante="primary"
          taille="lg"
          chargement={enCours}
          className="w-full"
        >
          Envoyer le lien
        </Bouton>
      </form>
    </Encadre>
  );
}

// ── Réinitialisation ─────────────────────────────────────────────────────────

export function Reinitialisation() {
  const { api } = useSession();
  const naviguer = useNavigate();
  const [parametres] = useSearchParams();
  const jeton = parametres.get('token');
  /*
   * Premier mot de passe d'un compte créé par la direction.
   *
   * Le même lien sert à la réinitialisation et à l'arrivée dans l'école : la
   * page disait « Nouveau mot de passe » à quelqu'un qui n'en avait jamais eu,
   * puis lui annonçait la fermeture de « sessions » qui n'existaient pas.
   */
  const bienvenue = parametres.get('bienvenue') === '1';
  const titre = bienvenue ? 'Choisissez votre mot de passe' : 'Nouveau mot de passe';

  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [fait, setFait] = useState(false);

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    // Vérifié côté client uniquement pour éviter un aller-retour : une faute
    // de frappe dans un mot de passe masqué ne se voit pas.
    if (motDePasse !== confirmation) {
      setErreur('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setErreur(null);
    setEnCours(true);
    try {
      await api.requete('/auth/reset-password', {
        methode: 'POST',
        corps: { token: jeton, password: motDePasse },
        sansRafraichissement: true,
      });
      setFait(true);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  if (!jeton) {
    return (
      <Encadre titre={titre}>
        <Message ton="erreur">Lien de réinitialisation incomplet.</Message>
      </Encadre>
    );
  }

  if (fait) {
    return (
      <Encadre titre={bienvenue ? 'Votre compte est prêt' : 'Mot de passe modifié'}>
        <div className="flex flex-col gap-4">
          <Message ton="succes">Votre mot de passe a été enregistré.</Message>
          {/* Point rarement dit, et pourtant rassurant : la réinitialisation
              ferme les autres sessions, donc un accès frauduleux en cours.
              Sans objet pour un compte qui n'a encore jamais servi. */}
          {!bienvenue && (
            <Message>
              Les sessions ouvertes sur vos autres appareils ont été fermées.
            </Message>
          )}
          <Bouton
            variante="primary"
            taille="lg"
            onClick={() => naviguer('/')}
            className="w-full"
          >
            Se connecter
          </Bouton>
        </div>
      </Encadre>
    );
  }

  return (
    <Encadre titre={titre}>
      <form onSubmit={soumettre} className="flex flex-col gap-4" noValidate>
        <Champ
          libelle={bienvenue ? 'Mot de passe' : 'Nouveau mot de passe'}
          type="password"
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
          autoComplete="new-password"
          aide={AIDE_MOT_DE_PASSE}
          minLength={LONGUEUR_MINIMALE_MOT_DE_PASSE}
          autoFocus
          required
        />
        <Champ
          libelle="Confirmation"
          type="password"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          autoComplete="new-password"
          required
        />
        {erreur && <Message ton="erreur">{erreur}</Message>}
        <Bouton
          type="submit"
          variante="primary"
          taille="lg"
          chargement={enCours}
          className="w-full"
        >
          Enregistrer
        </Bouton>
      </form>
    </Encadre>
  );
}

// ── Acceptation d'invitation ─────────────────────────────────────────────────

interface DetailInvitation {
  email: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  tenantName: string;
  expiresAt: string;
  accountExists: boolean;
}

const LIBELLE_ROLE: Record<DetailInvitation['role'], string> = {
  OWNER: 'propriétaire',
  ADMIN: 'administrateur',
  MEMBER: 'membre',
};

export function Invitation() {
  const { api, connecter } = useSession();
  const naviguer = useNavigate();
  const [parametres] = useSearchParams();
  const jeton = parametres.get('token');

  const [detail, setDetail] = useState<DetailInvitation | null>(null);
  const [chargement, setChargement] = useState(true);
  const [motDePasse, setMotDePasse] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!jeton) {
      setChargement(false);
      setErreur('Lien d\'invitation incomplet.');
      return;
    }
    let annule = false;
    void api
      .requete<DetailInvitation>(`/invitations/${encodeURIComponent(jeton)}`, {
        sansRafraichissement: true,
      })
      .then(
        (recu) => { if (!annule) { setDetail(recu); setChargement(false); } },
        (e: unknown) => {
          if (annule) return;
          setErreur(messageErreur(e));
          setChargement(false);
        },
      );
    return () => { annule = true; };
  }, [api, jeton]);

  async function accepter(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.requete('/invitations/accept', {
        methode: 'POST',
        // Le mot de passe n'est transmis que s'il sert à créer le compte :
        // l'envoyer pour un compte existant laisserait croire qu'il le
        // remplace.
        corps: {
          token: jeton,
          ...(detail?.accountExists
            ? {}
            : {
              password: motDePasse,
              firstName: prenom.trim(),
              lastName: nom.trim(),
            }),
        },
        sansRafraichissement: true,
      });
      // Le compte vient d'être créé : on connecte directement plutôt que de
      // faire ressaisir un mot de passe choisi dix secondes plus tôt.
      if (detail && !detail.accountExists) {
        await connecter(detail.email, motDePasse);
      }
      naviguer('/');
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  if (chargement) {
    return <Encadre titre="Invitation"><Message>Chargement…</Message></Encadre>;
  }

  if (!detail) {
    return (
      <Encadre titre="Invitation">
        <div className="flex flex-col gap-3">
          <Message ton="erreur">{erreur ?? 'Invitation introuvable.'}</Message>
          <Message>
            Demandez à l'école de vous envoyer une nouvelle invitation.
          </Message>
        </div>
      </Encadre>
    );
  }

  return (
    <Encadre titre="Rejoindre un espace">
      <form onSubmit={accepter} className="flex flex-col gap-4" noValidate>
        <Message>
          {`${detail.tenantName} vous invite à rejoindre son journal de classe `}
          {`en tant que ${LIBELLE_ROLE[detail.role]}, à l'adresse ${detail.email}.`}
        </Message>

        {detail.accountExists ? (
          <Message>
            Vous avez déjà un compte : acceptez, puis connectez-vous comme
            d'habitude.
          </Message>
        ) : (
          <>
            {/*
              * Le compte se CRÉE ici : c'est le seul moment où l'intéressé
              * peut se nommer lui-même. Une invitation ne porte que son
              * adresse — l'école ne l'a pas nommé à sa place, contrairement à
              * un compte créé directement.
              */}
            <GrilleFormulaire>
              <Champ
                libelle="Prénom"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                autoComplete="given-name"
                maxLength={60}
                autoFocus
                required
              />
              <Champ
                libelle="Nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                autoComplete="family-name"
                maxLength={60}
                required
              />
            </GrilleFormulaire>
            <Champ
              libelle="Choisissez un mot de passe"
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              autoComplete="new-password"
              aide={AIDE_MOT_DE_PASSE}
              minLength={LONGUEUR_MINIMALE_MOT_DE_PASSE}
              required
            />
          </>
        )}

        {erreur && <Message ton="erreur">{erreur}</Message>}
        <Bouton
          type="submit"
          variante="primary"
          taille="lg"
          chargement={enCours}
          className="w-full"
        >
          Rejoindre
        </Bouton>
      </form>
    </Encadre>
  );
}
